import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { db } from "../firebase";
import {
  collection, onSnapshot, query, where,
  addDoc, setDoc, deleteDoc, doc, getDocs, updateDoc,
  serverTimestamp,
} from "firebase/firestore";

const QUESTIONS_COL = "baptism_quiz_questions";
const RESPONSES_COL = "baptism_quiz_responses";
const LEADERBOARD_COL = "baptism_leaderboard";
const QUIZZES_COL = "baptism_quizzes";
const ATTENDEES_COL = "baptism_quiz_attendees";

const G = "#c9a55a";
const NAVY = "#12122a";
const TIMER_R = 20;
const TIMER_CIRC = 2 * Math.PI * TIMER_R;

const BADGE_COLORS = [
  "#fbbf24","#34d399","#60a5fa","#f472b6","#a78bfa",
  "#f87171","#2dd4bf","#fb923c","#818cf8","#a3e635",
  "#e879f9","#38bdf8","#4ade80","#facc15","#f97316",
];

// ── helpers ───────────────────────────────────────────────────────────────────

function arraysEqual(a, b) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function getAnswerResult(q, answer) {
  const type = q.inputType || "radio";
  if (answer === -1 || answer === undefined || answer === null) {
    return { correct: false, timedOut: true, display: "Timed out" };
  }
  if (type === "radio") {
    return { correct: answer === q.correctIndex, timedOut: false, display: q.options?.[answer] ?? "—" };
  }
  if (type === "checkbox") {
    const sel = (Array.isArray(answer) ? answer : []).slice().sort((a, b) => a - b);
    const exp = (q.correctIndices || []).slice().sort((a, b) => a - b);
    return { correct: arraysEqual(sel, exp), timedOut: false, display: sel.length ? sel.map((i) => q.options?.[i]).join(", ") : "—" };
  }
  const exp = (q.correctText || "").trim().toLowerCase();
  const given = (answer || "").toString().trim().toLowerCase();
  return { correct: exp === "" || given === exp, timedOut: false, display: answer || "—" };
}

// ── TimerRing ─────────────────────────────────────────────────────────────────

function TimerRing({ timeLeft, total }) {
  const pct = Math.max(0, timeLeft / total);
  const color = pct > 0.6 ? "#4ade80" : pct > 0.3 ? "#fbbf24" : "#f87171";
  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;
  return (
    <div className="relative flex items-center justify-center shrink-0" style={{ width: 64, height: 64 }}>
      <svg width="64" height="64" style={{ transform: "rotate(-90deg)", position: "absolute" }}>
        <circle cx="32" cy="32" r={TIMER_R} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3.5" />
        <circle cx="32" cy="32" r={TIMER_R} fill="none" stroke={color} strokeWidth="3.5"
          strokeDasharray={TIMER_CIRC} strokeDashoffset={TIMER_CIRC * (1 - pct)}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.9s linear, stroke 0.4s ease" }} />
      </svg>
      <span style={{ color, fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "0.8rem" }}
        className={timeLeft <= 30 ? "animate-pulse" : ""}>
        {mins}:{String(secs).padStart(2, "0")}
      </span>
    </div>
  );
}

// ── InputTypeBtn ──────────────────────────────────────────────────────────────

function InputTypeBtn({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{ border: `1px solid ${active ? G : `${G}35`}`, background: active ? `${G}18` : "transparent", color: active ? "#7a4f00" : "#9a8060" }}
      className="text-sm px-3 py-1.5 rounded-lg transition-all">
      {label}
    </button>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function BaptismQuiz() {
  const { quizId } = useParams();
  const navigate = useNavigate();

  const [quizMeta, setQuizMeta] = useState(null);
  // screens: "welcome" | "lobby" | "playing" | "result"
  const [screen, setScreen] = useState("welcome");
  const [guestName, setGuestName] = useState(() => localStorage.getItem("baptism_guest_name") || "");
  const [nameError, setNameError] = useState("");
  const [joining, setJoining] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const [questions, setQuestions] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);

  // answer state
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [checkboxSel, setCheckboxSel] = useState([]);
  const [textInput, setTextInput] = useState("");
  const [answerLocked, setAnswerLocked] = useState(false);

  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [finalResult, setFinalResult] = useState(null);

  // admin
  const [adminUnlocked] = useState(() => localStorage.getItem("baptism_admin_token") === "true");
  const [newQ, setNewQ] = useState({ question: "", inputType: "radio", options: ["", "", "", ""], correctIndex: 0, correctIndices: [], correctText: "", timeLimit: 0 });
  const [saving, setSaving] = useState(false);

  // refs
  const answersRef = useRef({});
  const scoreRef = useRef(0);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const isAnsweredRef = useRef(false);
  const scoreSubmittedRef = useRef(false);
  const currentQRef = useRef(0);
  const guestNameRef = useRef("");
  const questionsRef = useRef([]);
  const quizMetaRef = useRef(null);
  const checkboxSelRef = useRef([]);
  const textInputRef = useRef("");
  const screenRef = useRef("welcome");

  useEffect(() => { questionsRef.current = questions; }, [questions]);
  useEffect(() => { quizMetaRef.current = quizMeta; }, [quizMeta]);
  useEffect(() => { screenRef.current = screen; }, [screen]);

  // auto-start when admin fires the quiz
  useEffect(() => {
    if (quizMeta?.status === "playing" && screenRef.current === "lobby") {
      beginQuiz();
    }
  }, [quizMeta?.status]); // eslint-disable-line

  useEffect(() => {
    if (!quizId) return;
    if (!document.getElementById("baptism-fonts")) {
      const link = document.createElement("link");
      link.id = "baptism-fonts";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&display=swap";
      document.head.appendChild(link);
    }

    const unsubMeta = onSnapshot(doc(db, QUIZZES_COL, quizId), (snap) => {
      if (snap.exists()) setQuizMeta({ id: quizId, ...snap.data() });
    });

    const unsubQ = onSnapshot(
      query(collection(db, QUESTIONS_COL), where("quizId", "==", quizId)),
      (snap) => {
        const qs = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setQuestions(qs); questionsRef.current = qs;
      }
    );

    const unsubA = onSnapshot(
      query(collection(db, ATTENDEES_COL), where("quizId", "==", quizId)),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.joinedAt?.seconds ?? 0) - (b.joinedAt?.seconds ?? 0));
        setAttendees(list);
        const saved = localStorage.getItem("baptism_guest_name") || "";
        if (saved && list.some((a) => a.name.toLowerCase() === saved.toLowerCase()) && screenRef.current === "welcome") {
          guestNameRef.current = saved;
          setScreen("lobby");
        }
      }
    );

    return () => { unsubMeta(); unsubQ(); unsubA(); clearTimeout(timerRef.current); clearInterval(countdownRef.current); };
  }, [quizId]);

  // server-time sync — keeps all clients on the same question
  useEffect(() => {
    if (screen !== "playing" || !quizMeta?.startedAt) return;
    const startMs = quizMeta.startedAt.toMillis
      ? quizMeta.startedAt.toMillis()
      : quizMeta.startedAt.seconds * 1000;

    const sync = () => {
      const qs = questionsRef.current;
      const totalQs = qs.length;
      if (totalQs === 0) return;

      const elapsed = Math.max(0, Date.now() - startMs);

      // build cumulative start-time offsets for each question
      const offsets = [0];
      for (let i = 0; i < totalQs; i++) {
        offsets.push(offsets[i] + (qs[i].timeLimit || 20) * 1000);
      }

      // quiz finished
      if (elapsed >= offsets[totalQs]) {
        const lastQ = qs[totalQs - 1];
        if (lastQ && answersRef.current[lastQ.id] === undefined) {
          answersRef.current[lastQ.id] = -1;
        }
        if (!scoreSubmittedRef.current) {
          scoreSubmittedRef.current = true;
          submitScoreAndNavigate();
        }
        return;
      }

      // find which question we're on
      let qIndex = 0;
      while (qIndex < totalQs - 1 && elapsed >= offsets[qIndex + 1]) qIndex++;

      const qLimit = qs[qIndex]?.timeLimit || 20;
      const tLeft = Math.max(0, qLimit - Math.floor((elapsed - offsets[qIndex]) / 1000));
      setTimeLeft(tLeft);

      if (qIndex !== currentQRef.current) {
        const prevQ = qs[currentQRef.current];
        if (prevQ && answersRef.current[prevQ.id] === undefined) {
          answersRef.current[prevQ.id] = -1;
        }
        currentQRef.current = qIndex;
        isAnsweredRef.current = false;
        setCurrentQ(qIndex);
        setSelectedAnswer(null);
        setCheckboxSel([]); checkboxSelRef.current = [];
        setTextInput(""); textInputRef.current = "";
        setAnswerLocked(false);
      }
    };

    sync();
    countdownRef.current = setInterval(sync, 500);
    return () => clearInterval(countdownRef.current);
  }, [screen, quizMeta?.startedAt]); // eslint-disable-line

const submitScoreAndNavigate = async () => {
    const name = guestNameRef.current;
    const meta = quizMetaRef.current;
    const finalScore = scoreRef.current;
    const finalAnswers = { ...answersRef.current };
    try {
      await addDoc(collection(db, RESPONSES_COL), {
        quizId, quizTitle: meta?.title ?? "", guestName: name,
        answers: finalAnswers, score: finalScore,
        totalQuestions: questionsRef.current.length,
        submittedAt: serverTimestamp(),
      });
      const lbId = `${quizId}_${name.toLowerCase().replace(/\s+/g, "_")}`;
      await setDoc(doc(db, LEADERBOARD_COL, lbId), {
        name, score: finalScore, quizId, quizTitle: meta?.title ?? "",
        quizEmoji: meta?.emoji ?? "🎯", source: "quiz", addedAt: serverTimestamp(),
      });
    } catch (err) { console.error("Score submission failed:", err); }
    setFinalResult({ score: finalScore, answers: finalAnswers });
    setScreen("result");
  };

  // ── join flow ─────────────────────────────────────────────────────────────

  const joinQuiz = async () => {
    const name = guestName.trim();
    if (!name) { setNameError("Please enter your name."); return; }
    setJoining(true); setNameError("");
    try {
      const already = attendees.some((a) => a.name.toLowerCase() === name.toLowerCase());
      if (!already) {
        await addDoc(collection(db, ATTENDEES_COL), { quizId, name, joinedAt: serverTimestamp() });
      }
    } catch (err) { console.error("Attendee write failed:", err); }
    guestNameRef.current = name;
    localStorage.setItem("baptism_guest_name", name);
    setJoining(false);
    setScreen("lobby");
  };

  const beginQuiz = async () => {
    const name = guestNameRef.current || localStorage.getItem("baptism_guest_name") || "";
    if (!name || questionsRef.current.length === 0) return;
    const snap = await getDocs(
      query(collection(db, RESPONSES_COL), where("guestName", "==", name), where("quizId", "==", quizId))
    );
    if (!snap.empty) { setFinalResult(null); setScreen("result"); return; }
    answersRef.current = {}; scoreRef.current = 0;
    currentQRef.current = 0; isAnsweredRef.current = false;
    scoreSubmittedRef.current = false;
    setScore(0); setCurrentQ(0); setSelectedAnswer(null);
    setCheckboxSel([]); checkboxSelRef.current = [];
    setTextInput(""); textInputRef.current = "";
    setAnswerLocked(false);
    setScreen("playing");
  };

  // ── answer handlers ───────────────────────────────────────────────────────

  const handleRadioAnswer = (idx) => {
    if (isAnsweredRef.current) return;
    isAnsweredRef.current = true;
    const q = questionsRef.current[currentQRef.current];
    answersRef.current[q.id] = idx;
    scoreRef.current += idx === q.correctIndex ? (q.points || 10) : 0;
    setSelectedAnswer(idx); setScore(scoreRef.current); setAnswerLocked(true);
  };

  const handleCheckboxSubmit = () => {
    if (isAnsweredRef.current) return;
    isAnsweredRef.current = true;
    const q = questionsRef.current[currentQRef.current];
    const sel = [...checkboxSelRef.current];
    const exp = (q.correctIndices || []).slice().sort((a, b) => a - b);
    answersRef.current[q.id] = sel;
    scoreRef.current += arraysEqual(sel.slice().sort((a, b) => a - b), exp) ? (q.points || 10) : 0;
    setSelectedAnswer("submitted"); setScore(scoreRef.current); setAnswerLocked(true);
  };

  const handleTextSubmit = () => {
    if (isAnsweredRef.current || !textInputRef.current.trim()) return;
    isAnsweredRef.current = true;
    const q = questionsRef.current[currentQRef.current];
    const given = textInputRef.current.trim();
    const exp = (q.correctText || "").trim().toLowerCase();
    const correct = exp === "" || given.toLowerCase() === exp;
    answersRef.current[q.id] = given;
    scoreRef.current += correct ? (q.points || 10) : 0;
    setSelectedAnswer("submitted"); setScore(scoreRef.current); setAnswerLocked(true);
  };

  // ── admin helpers ─────────────────────────────────────────────────────────

  const startQuizForAll = () => updateDoc(doc(db, QUIZZES_COL, quizId), { status: "playing", startedAt: serverTimestamp() });
  const resetLobby = () => updateDoc(doc(db, QUIZZES_COL, quizId), { status: "waiting" });
  const clearAttendees = async () => {
    await Promise.all(attendees.map((a) => deleteDoc(doc(db, ATTENDEES_COL, a.id))));
  };

  const addQuestion = async () => {
    if (!newQ.question.trim()) return;
    if (newQ.inputType !== "text" && newQ.options.some((o) => !o.trim())) return;
    if (newQ.inputType === "checkbox" && newQ.correctIndices.length === 0) return;
    setSaving(true);
    try {
      const data = { quizId, question: newQ.question.trim(), inputType: newQ.inputType, points: 10, timeLimit: Number(newQ.timeLimit), order: questions.length + 1 };
      if (newQ.inputType === "radio") { data.options = newQ.options.map((o) => o.trim()); data.correctIndex = Number(newQ.correctIndex); }
      else if (newQ.inputType === "checkbox") { data.options = newQ.options.map((o) => o.trim()); data.correctIndices = newQ.correctIndices; }
      else { data.correctText = newQ.correctText.trim(); }
      await addDoc(collection(db, QUESTIONS_COL), data);
      setNewQ({ question: "", inputType: "radio", options: ["", "", "", ""], correctIndex: 0, correctIndices: [], correctText: "", timeLimit: 0 });
    } finally { setSaving(false); }
  };

  const deleteQuestion = (id) => deleteDoc(doc(db, QUESTIONS_COL, id));
  const toggleCorrectIndex = (i) =>
    setNewQ((p) => ({ ...p, correctIndices: p.correctIndices.includes(i) ? p.correctIndices.filter((x) => x !== i) : [...p.correctIndices, i] }));

  const quizUrl = `${window.location.origin}/baptism/quiz/${quizId}`;
  const isWaiting = !quizMeta?.status || quizMeta?.status === "waiting";

  // ── WELCOME ───────────────────────────────────────────────────────────────

  if (screen === "welcome") {
    const totalPts = questions.reduce((s, q) => s + (q.points || 10), 0);
    return (
      <div style={{ background: "linear-gradient(160deg,#fdf8ef,#fffcf6,#fdf5e6)", minHeight: "100vh", fontFamily: "'Cormorant Garamond',Georgia,serif" }}>
        <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
        <div className="max-w-lg mx-auto px-4 pt-14 pb-10">
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => navigate("/baptism/quiz")} style={{ color: `${G}80` }} className="text-sm hover:opacity-70 transition-opacity">
              ← All Quizzes
            </button>
            {adminUnlocked && (
              <span style={{ background: `${G}18`, border: `1px solid ${G}50`, color: G, letterSpacing: "0.1em" }} className="text-xs uppercase px-3 py-1.5 rounded-full font-medium">
                ⚙ Admin
              </span>
            )}
          </div>
          <div className="text-center mb-10">
            <div className="text-5xl mb-4">{quizMeta?.emoji ?? "🎯"}</div>
            <p style={{ letterSpacing: "0.25em", color: G }} className="text-xs uppercase mb-3">Baptism of Munachimso Zoe Maxwell</p>
            <h1 style={{ fontFamily: "'Playfair Display',serif", color: NAVY, lineHeight: 1.2 }} className="text-3xl sm:text-4xl font-bold mb-5">
              {quizMeta?.title ?? "Quiz"}
            </h1>
            {quizMeta?.description && <p style={{ color: "#9a8060" }} className="text-base mb-5">{quizMeta.description}</p>}
            <div className="flex items-center justify-center gap-3 max-w-xs mx-auto">
              <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,transparent,${G}50)` }} />
              <span style={{ color: G }}>✦</span>
              <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${G}50,transparent)` }} />
            </div>
          </div>

          <div className="flex justify-center mb-6">
            <div style={{ background: `${G}12`, border: `1px solid ${G}40` }} className="flex items-center gap-3 rounded-full px-6 py-2.5">
              <span style={{ color: G, fontFamily: "'Playfair Display',serif" }} className="font-bold text-lg">{questions.length}</span>
              <span style={{ color: "#7a6645" }} className="text-sm">question{questions.length !== 1 ? "s" : ""} · {totalPts} pts total</span>
            </div>
          </div>

          {/* QR toggle */}
          <div className="mb-6 text-center">
            <button onClick={() => setShowQR(!showQR)} style={{ border: `1px solid ${G}40`, color: G, background: `${G}10` }} className="text-sm px-5 py-2 rounded-full hover:opacity-80 transition-opacity">
              {showQR ? "Hide QR Code" : "📱 Show QR Code"}
            </button>
            {showQR && (
              <div className="mt-4 flex flex-col items-center gap-2">
                <div style={{ background: "#fff", borderRadius: 16, padding: 16, border: `2px solid ${G}40` }}>
                  <QRCodeSVG value={quizUrl} size={180} bgColor="#ffffff" fgColor={NAVY} level="M" />
                </div>
                <p style={{ color: `${G}80` }} className="text-xs">Scan to join this quiz</p>
              </div>
            )}
          </div>

          {/* Joined count */}
          {attendees.length > 0 && (
            <div style={{ background: `${G}10`, border: `1px solid ${G}30` }} className="rounded-2xl px-5 py-3 mb-5 flex items-center gap-3 flex-wrap">
              <span style={{ color: G }} className="text-sm font-medium shrink-0">{attendees.length} joined</span>
              <div className="flex gap-1.5 flex-wrap">
                {attendees.slice(-6).map((a) => (
                  <span key={a.id} style={{ background: `${G}18`, color: "#7a4f00", fontSize: "0.72rem" }} className="px-2.5 py-1 rounded-full">{a.name.split(" ")[0]}</span>
                ))}
              </div>
            </div>
          )}

          <div style={{ background: "rgba(255,255,255,0.96)", border: `1px solid ${G}28` }} className="rounded-3xl shadow-2xl p-8">
            <label style={{ color: "#7a6645", letterSpacing: "0.05em" }} className="text-sm font-medium block mb-1.5">Your Name *</label>
            <input type="text" placeholder="Enter your full name" value={guestName}
              onChange={(e) => { setGuestName(e.target.value); setNameError(""); }}
              onKeyDown={(e) => e.key === "Enter" && joinQuiz()}
              style={{ borderColor: `${G}40`, fontFamily: "'Cormorant Garamond',serif", fontSize: "1rem" }}
              className="w-full border rounded-xl px-4 py-3 focus:outline-none bg-amber-50/20 mb-4 transition-colors"
              onFocus={(e) => (e.target.style.borderColor = G)} onBlur={(e) => (e.target.style.borderColor = `${G}40`)}
            />
            {nameError && <p className="text-red-500 text-sm mb-3 -mt-2">{nameError}</p>}
            <button onClick={joinQuiz} disabled={joining}
              style={{ background: `linear-gradient(135deg,#c9a55a,#a07838)`, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.2em" }}
              className="w-full text-white font-semibold py-4 rounded-xl uppercase text-sm hover:opacity-90 active:opacity-75 transition-opacity shadow-md disabled:opacity-50">
              {joining ? "Joining…" : "Join Quiz"}
            </button>
          </div>
          <div className="text-center mt-6">
            <Link to="/baptism/leaderboard" style={{ color: `${G}80` }} className="text-sm hover:opacity-70 transition-opacity">View Leaderboard →</Link>
          </div>
        </div>

        {adminUnlocked && <div className="px-4 pb-12"><AdminPanel /></div>}
        <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
      </div>
    );
  }

  // ── LOBBY (waiting room) ──────────────────────────────────────────────────

  if (screen === "lobby") {
    const myName = guestNameRef.current || localStorage.getItem("baptism_guest_name") || "";
    return (
      <div style={{ background: "linear-gradient(160deg,#0d0d1e,#12122a,#0a0a18)", minHeight: "100vh", fontFamily: "'Cormorant Garamond',Georgia,serif" }}>
        <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
        <div className="max-w-2xl mx-auto px-4 py-14 text-center">
          {adminUnlocked && (
            <div className="flex justify-end mb-4">
              <span style={{ background: `${G}18`, border: `1px solid ${G}50`, color: G, letterSpacing: "0.1em" }} className="text-xs uppercase px-3 py-1.5 rounded-full font-medium">
                ⚙ Admin
              </span>
            </div>
          )}
          <div className="text-4xl mb-4">{quizMeta?.emoji ?? "🎯"}</div>
          <p style={{ letterSpacing: "0.2em", color: `${G}80` }} className="text-xs uppercase mb-2">Waiting Room</p>
          <h1 style={{ fontFamily: "'Playfair Display',serif", color: G, lineHeight: 1.2 }} className="text-3xl sm:text-4xl font-bold mb-2">
            {quizMeta?.title}
          </h1>
          <p style={{ color: "#6b6b8a", fontStyle: "italic" }} className="text-sm mb-10">
            Waiting for the host to start…
          </p>

          {/* Pulsing indicator */}
          <div className="flex justify-center items-center gap-2 mb-10">
            {[0, 1, 2].map((i) => (
              <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: G, opacity: 0.7, animationDelay: `${i * 0.25}s` }} className="animate-bounce" />
            ))}
          </div>

          {/* Participant badges */}
          <div style={{ border: `1px solid ${G}25`, background: "rgba(255,255,255,0.03)" }} className="rounded-3xl p-6 mb-8">
            <p style={{ color: `${G}70`, letterSpacing: "0.12em" }} className="text-xs uppercase mb-5">
              {attendees.length} {attendees.length === 1 ? "player" : "players"} joined
            </p>
            {attendees.length === 0 ? (
              <p style={{ color: "#4b4b6b", fontStyle: "italic" }} className="text-sm">Be the first to join!</p>
            ) : (
              <div className="flex flex-wrap gap-2 justify-center">
                {attendees.map((a, i) => {
                  const color = BADGE_COLORS[i % BADGE_COLORS.length];
                  const isMe = a.name.toLowerCase() === myName.toLowerCase();
                  return (
                    <span key={a.id}
                      style={{
                        background: `${color}18`,
                        border: `1.5px solid ${isMe ? color : color + "60"}`,
                        color: isMe ? color : `${color}cc`,
                        fontFamily: "'Cormorant Garamond',serif",
                        fontSize: isMe ? "0.95rem" : "0.88rem",
                        fontWeight: isMe ? 600 : 400,
                        boxShadow: isMe ? `0 0 10px ${color}30` : "none",
                        transition: "all 0.3s ease",
                      }}
                      className="px-4 py-2 rounded-full flex items-center gap-1.5"
                    >
                      {isMe && <span style={{ fontSize: "0.7rem" }}>★</span>}
                      {a.name}
                      {isMe && <span style={{ fontSize: "0.65rem", opacity: 0.7 }}> (you)</span>}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <p style={{ color: "#4b4b6b" }} className="text-xs">The quiz will begin automatically when the host presses Start.</p>
        </div>

        {adminUnlocked && (
          <div className="pb-12 px-4 max-w-md mx-auto space-y-3">
            <div style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${G}30` }} className="rounded-2xl p-5">
              <p style={{ color: `${G}80`, letterSpacing: "0.1em" }} className="text-xs uppercase mb-4">Quiz Controls</p>
              <div className="space-y-3">
                {isWaiting ? (
                  <button onClick={startQuizForAll}
                    style={{ background: `linear-gradient(135deg,#c9a55a,#a07838)`, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.15em" }}
                    className="w-full text-white font-semibold py-4 rounded-xl uppercase text-sm hover:opacity-90 transition-opacity shadow-lg">
                    🚀 Start Quiz for Everyone ({attendees.length})
                  </button>
                ) : (
                  <button onClick={resetLobby} style={{ border: `1px solid ${G}50`, color: G }}
                    className="w-full py-3 rounded-xl text-sm hover:opacity-80 transition-opacity">
                    ⏸ Reset to Lobby
                  </button>
                )}
                <button onClick={clearAttendees} style={{ color: "#f87171" }} className="w-full text-xs py-2 hover:opacity-70 transition-opacity">
                  Clear all attendees
                </button>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${G}20` }} className="rounded-2xl p-4 flex items-center gap-4">
              <div style={{ background: "#fff", borderRadius: 10, padding: 10, flexShrink: 0 }}>
                <QRCodeSVG value={quizUrl} size={72} bgColor="#ffffff" fgColor={NAVY} level="M" />
              </div>
              <p style={{ color: "#6b6b8a", wordBreak: "break-all" }} className="text-xs font-mono">{quizUrl}</p>
            </div>
          </div>
        )}
        <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
      </div>
    );
  }

  // ── PLAYING ───────────────────────────────────────────────────────────────

  if (screen === "playing") {
    const q = questions[currentQ];
    const type = q?.inputType || "radio";
    const progressPct = (currentQ / questions.length) * 100;
    const timedOut = selectedAnswer === -1;
    const answered = selectedAnswer !== null;
    const limit = q?.timeLimit || 0;

    let feedbackMsg = null, feedbackColor = "#fca5a5";
    if (answered) {
      if (timedOut) {
        feedbackMsg = `⏰ Time's up!${type !== "text" ? ` Answer: ${type === "radio" ? q.options[q.correctIndex] : (q.correctIndices || []).map((i) => q.options[i]).join(", ")}` : q.correctText ? ` Expected: "${q.correctText}"` : ""}`;
      } else if (type === "radio") {
        const correct = selectedAnswer === q.correctIndex;
        feedbackColor = correct ? "#86efac" : "#fca5a5";
        feedbackMsg = correct ? `🎉 Correct! +${q.points || 10} points` : `The answer was: ${q.options[q.correctIndex]}`;
      } else if (type === "checkbox") {
        const sel = checkboxSelRef.current.slice().sort((a, b) => a - b);
        const exp = (q.correctIndices || []).slice().sort((a, b) => a - b);
        const correct = arraysEqual(sel, exp);
        feedbackColor = correct ? "#86efac" : "#fca5a5";
        feedbackMsg = correct ? `🎉 Correct! +${q.points || 10} points` : `Correct: ${exp.map((i) => q.options[i]).join(", ")}`;
      } else {
        const exp = (q.correctText || "").trim().toLowerCase();
        const given = textInputRef.current.trim().toLowerCase();
        const correct = exp === "" || given === exp;
        feedbackColor = correct ? "#86efac" : "#fca5a5";
        feedbackMsg = correct ? `🎉 Correct! +${q.points || 10} points` : (exp ? `Expected: "${q.correctText}"` : `Got it! +${q.points || 10} points`);
        if (!exp) feedbackColor = "#86efac";
      }
    }

    return (
      <div style={{ background: "linear-gradient(160deg,#0d0d1e,#12122a,#0a0a18)", minHeight: "100vh", fontFamily: "'Cormorant Garamond',Georgia,serif" }}>
        <div style={{ height: 4, background: "rgba(255,255,255,0.08)" }}>
          <div style={{ width: `${progressPct}%`, height: "100%", background: `linear-gradient(90deg,${G},#f59e0b)`, transition: "width 0.5s ease" }} />
        </div>
        <div className="max-w-lg mx-auto px-4 pt-10 pb-20">
          <div className="flex justify-between items-center mb-10">
            <div>
              <span style={{ color: `${G}70`, letterSpacing: "0.15em" }} className="text-xs uppercase block">{currentQ + 1} / {questions.length}</span>
              <span style={{ color: `${G}50` }} className="text-xs">{quizMeta?.emoji} {quizMeta?.title}</span>
            </div>
            <div className="flex items-center gap-3">
              {limit > 0 && timeLeft !== null && <TimerRing timeLeft={timeLeft} total={limit} />}
              <div style={{ background: `${G}18`, border: `1px solid ${G}40` }} className="flex items-center gap-2 rounded-full px-4 py-1.5">
                <span style={{ color: G, fontFamily: "'Playfair Display',serif" }} className="font-bold tabular-nums">{score}</span>
                <span style={{ color: `${G}60` }} className="text-xs">pts</span>
              </div>
            </div>
          </div>

          <div className="text-center mb-8">
            {type !== "radio" && (
              <span style={{ background: `${G}20`, color: G, letterSpacing: "0.1em" }} className="text-xs uppercase px-3 py-1 rounded-full mb-4 inline-block">
                {type === "checkbox" ? "☑ Select all that apply" : "✏ Free text answer"}
              </span>
            )}
            <h2 style={{ fontFamily: "'Playfair Display',serif", color: "#f1f5f9", lineHeight: 1.35 }} className="text-2xl sm:text-3xl font-semibold">
              {q.question}
            </h2>
          </div>

          {type === "radio" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {q.options?.map((opt, idx) => {
                let bg = "rgba(255,255,255,0.05)", border = "rgba(255,255,255,0.12)", color = "#e2e8f0", icon = String.fromCharCode(65 + idx);
                if (answered) {
                  if (idx === q.correctIndex) { bg = "rgba(22,163,74,0.18)"; border = "#16a34a"; color = "#86efac"; icon = "✓"; }
                  else if (idx === selectedAnswer && !timedOut) { bg = "rgba(220,38,38,0.18)"; border = "#dc2626"; color = "#fca5a5"; icon = "✗"; }
                  else { bg = "rgba(255,255,255,0.02)"; border = "rgba(255,255,255,0.05)"; color = "#4b4b6b"; }
                }
                return (
                  <button key={idx} onClick={() => handleRadioAnswer(idx)} disabled={answered}
                    style={{ background: bg, border: `2px solid ${border}`, color, fontFamily: "'Cormorant Garamond',serif", fontSize: "1rem", transition: "all 0.25s ease", cursor: answered ? "default" : "pointer" }}
                    className="rounded-2xl px-5 py-4 text-left flex items-center gap-3">
                    <span style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, fontSize: "0.85rem", width: 20, flexShrink: 0 }}>{icon}</span>
                    {opt}
                  </button>
                );
              })}
            </div>
          )}

          {type === "checkbox" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {q.options?.map((opt, idx) => {
                  const isSel = checkboxSel.includes(idx);
                  const isCorrect = (q.correctIndices || []).includes(idx);
                  let bg, border, color, icon;
                  if (!answered) { bg = isSel ? `${G}18` : "rgba(255,255,255,0.05)"; border = isSel ? G : "rgba(255,255,255,0.12)"; color = isSel ? "#f1f5f9" : "#e2e8f0"; icon = isSel ? "☑" : "☐"; }
                  else {
                    if (isCorrect && isSel) { bg = "rgba(22,163,74,0.18)"; border = "#16a34a"; color = "#86efac"; icon = "✓"; }
                    else if (isCorrect && !isSel) { bg = "rgba(22,163,74,0.08)"; border = "#16a34a60"; color = "#86efac80"; icon = "✓"; }
                    else if (!isCorrect && isSel) { bg = "rgba(220,38,38,0.18)"; border = "#dc2626"; color = "#fca5a5"; icon = "✗"; }
                    else { bg = "rgba(255,255,255,0.02)"; border = "rgba(255,255,255,0.05)"; color = "#4b4b6b"; icon = "○"; }
                  }
                  return (
                    <button key={idx} disabled={answered}
                      onClick={() => { const next = checkboxSel.includes(idx) ? checkboxSel.filter((i) => i !== idx) : [...checkboxSel, idx]; setCheckboxSel(next); checkboxSelRef.current = next; }}
                      style={{ background: bg, border: `2px solid ${border}`, color, fontFamily: "'Cormorant Garamond',serif", fontSize: "1rem", transition: "all 0.2s ease", cursor: answered ? "default" : "pointer" }}
                      className="rounded-2xl px-5 py-4 text-left flex items-center gap-3">
                      <span style={{ fontSize: "1rem", width: 20, flexShrink: 0 }}>{icon}</span>{opt}
                    </button>
                  );
                })}
              </div>
              {!answered && (
                <button onClick={handleCheckboxSubmit} disabled={checkboxSel.length === 0}
                  style={{ background: `linear-gradient(135deg,#c9a55a,#a07838)`, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.15em" }}
                  className="w-full text-white font-semibold py-3.5 rounded-xl uppercase text-sm disabled:opacity-40 hover:opacity-90 transition-opacity shadow-md">
                  Submit Answer
                </button>
              )}
            </>
          )}

          {type === "text" && (
            <div className="space-y-3">
              <input type="text" placeholder="Type your answer here…" value={textInput} disabled={answered}
                onChange={(e) => { setTextInput(e.target.value); textInputRef.current = e.target.value; }}
                onKeyDown={(e) => e.key === "Enter" && handleTextSubmit()}
                style={{ borderColor: answered ? "rgba(255,255,255,0.15)" : `${G}50`, background: "rgba(255,255,255,0.06)", color: "#f1f5f9", fontFamily: "'Cormorant Garamond',serif", fontSize: "1.1rem" }}
                className="w-full border-2 rounded-2xl px-5 py-4 focus:outline-none placeholder:text-gray-600 transition-colors"
                onFocus={(e) => { if (!answered) e.target.style.borderColor = G; }}
                onBlur={(e) => { if (!answered) e.target.style.borderColor = `${G}50`; }}
              />
              {!answered && (
                <button onClick={handleTextSubmit} disabled={!textInput.trim()}
                  style={{ background: `linear-gradient(135deg,#c9a55a,#a07838)`, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.15em" }}
                  className="w-full text-white font-semibold py-3.5 rounded-xl uppercase text-sm disabled:opacity-40 hover:opacity-90 transition-opacity shadow-md">
                  Submit Answer
                </button>
              )}
            </div>
          )}

          {feedbackMsg && <p style={{ color: feedbackColor, fontStyle: "italic" }} className="text-center mt-6 text-base">{feedbackMsg}</p>}
        </div>
      </div>
    );
  }

  // ── RESULT ────────────────────────────────────────────────────────────────

  const totalPossible = questions.reduce((s, q) => s + (q.points || 10), 0);
  const correctCount = questions.filter((q) => getAnswerResult(q, finalResult?.answers[q.id]).correct).length;
  const pct = totalPossible > 0 ? Math.round(((finalResult?.score ?? 0) / totalPossible) * 100) : 0;
  const emoji = pct >= 80 ? "🏆" : pct >= 50 ? "🎉" : "💪";
  const message = pct >= 80 ? "Outstanding! 🌟" : pct >= 50 ? "Great effort!" : "Keep showing up — that's what counts! 💛";
  const myName = guestNameRef.current || localStorage.getItem("baptism_guest_name") || "You";

  return (
    <div style={{ background: "linear-gradient(160deg,#0d0d1e,#12122a,#0a0a18)", minHeight: "100vh", fontFamily: "'Cormorant Garamond',Georgia,serif" }}>
      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <div className="text-6xl mb-4">{emoji}</div>
        <p style={{ color: `${G}70`, letterSpacing: "0.15em" }} className="text-xs uppercase mb-2">{quizMeta?.title}</p>
        <p style={{ letterSpacing: "0.2em", color: `${G}80` }} className="text-xs uppercase mb-3">Well done, {myName.split(" ")[0]}!</p>
        <div style={{ fontFamily: "'Playfair Display',serif", color: G }} className="text-6xl font-bold mb-1">{finalResult?.score ?? 0}</div>
        <p style={{ color: "#6b6b8a" }} className="text-sm mb-8">out of {totalPossible} points · {correctCount} / {questions.length} correct</p>
        <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 99, overflow: "hidden" }} className="h-3 mb-3">
          <div style={{ width: `${pct}%`, height: "100%", background: `linear-gradient(90deg,${G},#f59e0b)`, borderRadius: 99, transition: "width 1.2s ease" }} />
        </div>
        <p style={{ color: `${G}60` }} className="text-xs mb-10">{pct}%</p>
        <p style={{ color: "#9a8060", fontStyle: "italic" }} className="text-base mb-12">{message}</p>

        <div className="space-y-2 mb-12 text-left">
          {questions.map((q, i) => {
            const answer = finalResult?.answers[q.id];
            const { correct, timedOut, display } = getAnswerResult(q, answer);
            const type = q.inputType || "radio";
            const correctDisplay = type === "radio" ? q.options?.[q.correctIndex] : type === "checkbox" ? (q.correctIndices || []).map((i) => q.options?.[i]).join(", ") : q.correctText || "Any answer";
            return (
              <div key={q.id} style={{ background: correct ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.08)", border: `1px solid ${correct ? "rgba(22,163,74,0.3)" : "rgba(220,38,38,0.25)"}` }} className="rounded-xl px-4 py-3">
                <p style={{ color: "#94a3b8" }} className="text-xs mb-1">Q{i + 1}. {q.question}</p>
                <p style={{ color: correct ? "#86efac" : "#fca5a5" }} className="text-sm font-medium">
                  {timedOut ? "⏰" : correct ? "✓" : "✗"} {display}
                  {!correct && <span style={{ color: "#6b6b8a" }} className="ml-2 text-xs">(correct: {correctDisplay})</span>}
                </p>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/baptism/leaderboard" style={{ background: `linear-gradient(135deg,#c9a55a,#a07838)`, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.15em" }} className="inline-block text-white font-semibold py-4 px-8 rounded-xl uppercase text-sm hover:opacity-90 transition-opacity shadow-lg">
            🏆 Leaderboard
          </Link>
          <Link to="/baptism/quiz" style={{ border: `1px solid ${G}40`, color: G, fontFamily: "'Cormorant Garamond',serif" }} className="inline-block py-4 px-8 rounded-xl text-sm hover:opacity-80 transition-opacity">
            Play Another Quiz
          </Link>
        </div>
      </div>
      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
    </div>
  );

  // ── Admin panel component (rendered inside welcome + lobby) ───────────────

  function AdminPanel() {
    return (
      <div className="max-w-2xl mx-auto mt-6 text-left">
        <div style={{ background: "rgba(255,255,255,0.97)", border: `1px solid ${G}30` }} className="rounded-2xl p-6 shadow-xl space-y-6">
          <h3 style={{ color: NAVY, fontFamily: "'Playfair Display',serif" }} className="text-lg font-semibold">
            {quizMeta?.emoji} {quizMeta?.title} — Admin
          </h3>

          {/* Quiz controls */}
          <div style={{ border: `1px solid ${G}30`, background: `${G}06` }} className="rounded-xl p-4 space-y-3">
            <p style={{ color: "#7a6645", letterSpacing: "0.1em" }} className="text-xs uppercase font-medium">Quiz Controls</p>
            <div className="flex gap-3 flex-wrap">
              {isWaiting ? (
                <button onClick={startQuizForAll}
                  style={{ background: `linear-gradient(135deg,#c9a55a,#a07838)` }}
                  className="flex-1 text-white font-semibold py-3 rounded-xl text-sm hover:opacity-90 transition-opacity">
                  🚀 Start Quiz for Everyone ({attendees.length})
                </button>
              ) : (
                <button onClick={resetLobby} style={{ border: `1px solid ${G}50`, color: "#7a4f00" }} className="flex-1 py-3 rounded-xl text-sm hover:opacity-80 transition-opacity">
                  ⏸ Reset to Lobby
                </button>
              )}
              <button onClick={clearAttendees} style={{ border: "1px solid #fca5a5", color: "#dc2626" }} className="py-3 px-4 rounded-xl text-sm hover:opacity-80 transition-opacity">
                Clear attendees
              </button>
            </div>
          </div>

          {/* QR code */}
          <div style={{ border: `1px solid ${G}30`, background: `${G}06` }} className="rounded-xl p-5">
            <p style={{ color: "#7a6645", letterSpacing: "0.1em" }} className="text-xs uppercase font-medium mb-4">📱 QR Code — Share to Join</p>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div style={{ background: "#fff", borderRadius: 12, padding: 14, border: `2px solid ${G}40`, flexShrink: 0 }}>
                <QRCodeSVG value={quizUrl} size={160} bgColor="#ffffff" fgColor={NAVY} level="M" />
              </div>
              <div className="flex-1 min-w-0">
                <p style={{ color: "#9a8060", wordBreak: "break-all" }} className="text-xs mb-3 font-mono">{quizUrl}</p>
                <p style={{ color: "#9a8060", letterSpacing: "0.08em" }} className="text-xs uppercase mb-2">Joined ({attendees.length})</p>
                {attendees.length === 0 ? (
                  <p style={{ color: `${G}60`, fontStyle: "italic" }} className="text-sm">Waiting for guests to scan…</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {attendees.map((a, i) => {
                      const color = BADGE_COLORS[i % BADGE_COLORS.length];
                      return (
                        <span key={a.id} style={{ background: `${color}18`, border: `1px solid ${color}60`, color: "#7a4f00", fontSize: "0.75rem" }} className="px-2.5 py-1 rounded-full">
                          {a.name}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Questions list */}
          <h4 style={{ color: NAVY, fontFamily: "'Playfair Display',serif" }} className="text-base font-semibold -mb-2">Questions</h4>
          {questions.length > 0 && (
            <div className="space-y-3">
              {questions.map((q, i) => {
                const type = q.inputType || "radio";
                const badge = type === "radio" ? "◉ Single" : type === "checkbox" ? "☑ Multi" : "✏ Text";
                return (
                  <div key={q.id} style={{ background: `${G}08`, border: `1px solid ${G}25` }} className="rounded-xl p-4">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span style={{ background: `${G}20`, color: "#7a4f00" }} className="text-xs px-2 py-0.5 rounded-full">{badge}</span>
                          <p style={{ color: NAVY }} className="font-medium text-sm">{i + 1}. {q.question}</p>
                        </div>
                        {type === "text" ? (
                          <p style={{ color: "#9a8060" }} className="text-xs">Expected: {q.correctText ? `"${q.correctText}"` : "Any answer"}</p>
                        ) : (
                          <div className="grid grid-cols-2 gap-1">
                            {q.options?.map((opt, oi) => {
                              const isCorrect = type === "radio" ? oi === q.correctIndex : (q.correctIndices || []).includes(oi);
                              return <p key={oi} style={{ color: isCorrect ? "#16a34a" : "#9a8060" }} className="text-xs">{isCorrect ? "✓" : "○"} {opt}</p>;
                            })}
                          </div>
                        )}
                        <p style={{ color: G }} className="text-xs mt-1.5">{q.points || 10} pts · {q.timeLimit ? `⏱ ${Math.floor(q.timeLimit / 60)}:${String(q.timeLimit % 60).padStart(2, "0")}` : "No limit"}</p>
                      </div>
                      <button onClick={() => deleteQuestion(q.id)} className="text-red-400 hover:text-red-600 text-xl leading-none shrink-0">×</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add question form */}
          <div style={{ border: `1px solid ${G}30` }} className="rounded-xl p-5 space-y-4">
            <p style={{ color: "#7a6645", letterSpacing: "0.1em" }} className="text-xs uppercase font-medium">Add New Question</p>
            <div>
              <label style={{ color: "#7a6645", letterSpacing: "0.08em" }} className="text-xs uppercase block mb-2">Response Type</label>
              <div className="flex gap-2 flex-wrap">
                <InputTypeBtn label="◉ Single Choice" active={newQ.inputType === "radio"} onClick={() => setNewQ((p) => ({ ...p, inputType: "radio", correctIndices: [] }))} />
                <InputTypeBtn label="☑ Multiple Choice" active={newQ.inputType === "checkbox"} onClick={() => setNewQ((p) => ({ ...p, inputType: "checkbox", correctIndex: 0 }))} />
                <InputTypeBtn label="✏ Free Text" active={newQ.inputType === "text"} onClick={() => setNewQ((p) => ({ ...p, inputType: "text", correctIndex: 0, correctIndices: [] }))} />
              </div>
            </div>
            <textarea rows={2} placeholder="Question text…" value={newQ.question}
              onChange={(e) => setNewQ((p) => ({ ...p, question: e.target.value }))}
              style={{ borderColor: `${G}40`, fontFamily: "'Cormorant Garamond',serif", fontSize: "0.95rem" }}
              className="w-full border rounded-xl px-4 py-2.5 focus:outline-none resize-none bg-amber-50/20" />
            {newQ.inputType === "radio" && (
              <div className="grid grid-cols-2 gap-3">
                {newQ.options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="radio" name="correctIndex" checked={newQ.correctIndex === i} onChange={() => setNewQ((p) => ({ ...p, correctIndex: i }))} style={{ accentColor: G, width: 16, height: 16, flexShrink: 0 }} />
                    <input type="text" placeholder={`Option ${i + 1}`} value={opt}
                      onChange={(e) => setNewQ((p) => ({ ...p, options: p.options.map((o, oi) => oi === i ? e.target.value : o) }))}
                      style={{ borderColor: newQ.correctIndex === i ? "#16a34a" : `${G}35`, fontFamily: "'Cormorant Garamond',serif", fontSize: "0.9rem" }}
                      className="flex-1 border rounded-lg px-3 py-2 focus:outline-none bg-amber-50/20 text-sm min-w-0" />
                  </div>
                ))}
              </div>
            )}
            {newQ.inputType === "checkbox" && (
              <div className="grid grid-cols-2 gap-3">
                {newQ.options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="checkbox" checked={newQ.correctIndices.includes(i)} onChange={() => toggleCorrectIndex(i)} style={{ accentColor: G, width: 16, height: 16, flexShrink: 0 }} />
                    <input type="text" placeholder={`Option ${i + 1}`} value={opt}
                      onChange={(e) => setNewQ((p) => ({ ...p, options: p.options.map((o, oi) => oi === i ? e.target.value : o) }))}
                      style={{ borderColor: newQ.correctIndices.includes(i) ? "#16a34a" : `${G}35`, fontFamily: "'Cormorant Garamond',serif", fontSize: "0.9rem" }}
                      className="flex-1 border rounded-lg px-3 py-2 focus:outline-none bg-amber-50/20 text-sm min-w-0" />
                  </div>
                ))}
              </div>
            )}
            {newQ.inputType === "text" && (
              <div>
                <label style={{ color: "#7a6645", letterSpacing: "0.08em" }} className="text-xs uppercase block mb-1.5">Expected Answer (optional)</label>
                <input type="text" placeholder='e.g. "Steinkjer"' value={newQ.correctText}
                  onChange={(e) => setNewQ((p) => ({ ...p, correctText: e.target.value }))}
                  style={{ borderColor: `${G}40`, fontFamily: "'Cormorant Garamond',serif", fontSize: "0.95rem" }}
                  className="w-full border rounded-xl px-4 py-2.5 focus:outline-none bg-amber-50/20" />
                <p style={{ color: "#9a8060" }} className="text-xs mt-1">Leave empty to award full points to all answers.</p>
              </div>
            )}
            <div>
              <label style={{ color: "#7a6645", letterSpacing: "0.08em" }} className="text-xs uppercase block mb-2">⏱ Time Limit</label>
              <div className="flex gap-2 flex-wrap">
                {[{ value: 0, label: "No limit" }, { value: 60, label: "1 min" }, { value: 90, label: "1.5 min" }, { value: 120, label: "2 min" }, { value: 180, label: "3 min" }, { value: 300, label: "5 min" }].map(({ value, label }) => (
                  <button key={value} type="button" onClick={() => setNewQ((p) => ({ ...p, timeLimit: value }))}
                    style={{ border: `1px solid ${newQ.timeLimit === value ? G : `${G}35`}`, background: newQ.timeLimit === value ? `${G}18` : "transparent", color: newQ.timeLimit === value ? "#7a4f00" : "#9a8060" }}
                    className="text-sm px-3 py-1.5 rounded-lg transition-all">{label}</button>
                ))}
              </div>
            </div>
            <button onClick={addQuestion}
              disabled={saving || !newQ.question.trim() || (newQ.inputType !== "text" && newQ.options.some((o) => !o.trim())) || (newQ.inputType === "checkbox" && newQ.correctIndices.length === 0)}
              style={{ background: `linear-gradient(135deg,#c9a55a,#a07838)` }}
              className="w-full text-white font-semibold py-3 rounded-xl text-sm disabled:opacity-40 hover:opacity-90 transition-opacity">
              {saving ? "Saving…" : "Add Question"}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
